import { useEffect, useState, useCallback } from "react";
import { Users, Wifi, MessageSquare, ListChecks, Video, Paperclip, Megaphone, Pencil, Trash2, KeyRound, Download, Loader2, Shield, Check, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { relTime, formatApiErrorDetail } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-[#2E3242] bg-[#1E2029] p-4">
      <div className="flex items-center gap-2 text-gray-500 text-[11px] uppercase tracking-wider">
        <Icon className={`w-3.5 h-3.5 ${accent}`} /> {label}
      </div>
      <p className="mt-1.5 font-display font-bold text-2xl text-gray-100">{value}</p>
    </div>
  );
}

function EditUserDialog({ target, open, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) { setName(target.name); setEmail(target.email); setRole(target.role); setPwd(""); }
  }, [target]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/users/${target.id}`, { name, email, role });
      if (pwd.trim()) await api.post(`/admin/users/${target.id}/password`, { password: pwd.trim() });
      toast.success("User updated");
      onSaved?.(r.data.user);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update user");
    } finally {
      setBusy(false);
    }
  };

  if (!target) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[#16181F] border-[#2E3242] text-gray-200" data-testid="admin-edit-user-dialog">
        <DialogTitle className="font-display font-bold text-lg">Edit {target.name}</DialogTitle>
        <DialogDescription className="text-gray-500 text-sm">Change this teammate's details.</DialogDescription>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs text-gray-400">Name</label>
            <Input data-testid="admin-edit-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-[#0F1015] border-[#2E3242] focus-visible:ring-red-600 h-10" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Email</label>
            <Input data-testid="admin-edit-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-[#0F1015] border-[#2E3242] focus-visible:ring-red-600 h-10" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Role</label>
            <div className="mt-1 flex gap-2">
              {["member", "admin"].map((r) => (
                <button key={r} data-testid={`admin-role-${r}`} onClick={() => setRole(r)}
                  className={`flex-1 h-10 rounded-md text-sm capitalize border transition-colors ${role === r ? "ruby-gradient text-white border-red-900/40" : "bg-[#0F1015] border-[#2E3242] text-gray-400 hover:text-gray-200"}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400">Reset password (optional)</label>
            <Input data-testid="admin-edit-password" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Leave blank to keep current" className="mt-1 bg-[#0F1015] border-[#2E3242] focus-visible:ring-red-600 h-10" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Button data-testid="admin-save-user" onClick={save} disabled={busy} className="ruby-gradient text-white border border-red-900/40 h-10 flex-1">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
          <button onClick={() => onOpenChange(false)} className="h-10 px-3 text-sm text-gray-400 hover:text-gray-200">Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ stats: {}, users: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [announce, setAnnounce] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/overview");
      setData(r.data);
    } catch (e) {
      toast.error("Could not load the admin dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const int = setInterval(load, 10000);
    return () => clearInterval(int);
  }, [load]);

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setData((d) => ({ ...d, users: d.users.filter((x) => x.id !== u.id) }));
      toast.success("User deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete user");
    }
  };

  const sendAnnounce = async () => {
    const c = announce.trim();
    if (!c) return;
    setSending(true);
    try {
      await api.post("/admin/announce", { content: c });
      setAnnounce("");
      toast.success("Announcement posted to #general");
    } catch (e) {
      toast.error("Could not post the announcement");
    } finally {
      setSending(false);
    }
  };

  const exportCsv = () => {
    const rows = [["name", "email", "role", "online", "messages", "dms", "tasks_created", "last_seen"]];
    data.users.forEach((u) => rows.push([u.name, u.email, u.role, u.online, u.message_count, u.dm_count, u.tasks_created, u.last_seen]));
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "reciperemedy-users.csv";
    a.click();
  };

  const s = data.stats;

  return (
    <div className="flex-1 min-w-0 bg-[#16181F] overflow-y-auto" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-xl ruby-gradient flex items-center justify-center ember-glow">
            <Shield className="w-5 h-5 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-bold text-xl text-gray-100">Admin Dashboard</h1>
            <p className="text-sm text-gray-500">Manage members and monitor the workspace.</p>
          </div>
          <Button data-testid="admin-export-csv" onClick={exportCsv} className="bg-[#262936] hover:bg-[#30333f] text-gray-100 border border-[#2E3242] h-9">
            <Download className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Export CSV</span>
          </Button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto text-red-400" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat icon={Users} label="Members" value={s.total_users} accent="text-red-400" />
              <Stat icon={Wifi} label="Online" value={s.online_users} accent="text-emerald-400" />
              <Stat icon={MessageSquare} label="Messages" value={s.total_messages} accent="text-blue-400" />
              <Stat icon={MessageSquare} label="Direct msgs" value={s.total_dms} accent="text-violet-400" />
              <Stat icon={ListChecks} label="Open tasks" value={s.tasks_open} accent="text-amber-400" />
              <Stat icon={ListChecks} label="Active tasks" value={s.tasks_in_progress} accent="text-blue-400" />
              <Stat icon={ListChecks} label="Done tasks" value={s.tasks_done} accent="text-emerald-400" />
              <Stat icon={Video} label="Recordings" value={s.total_meetings} accent="text-red-400" />
            </div>

            <div className="mt-6 rounded-xl border border-[#2E3242] bg-[#1E2029] p-4">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
                <Megaphone className="w-3.5 h-3.5 text-red-400" /> Broadcast announcement to #general
              </p>
              <div className="flex gap-2">
                <Input data-testid="admin-announce-input" value={announce} onChange={(e) => setAnnounce(e.target.value)}
                  placeholder="Type a team-wide announcement…" className="bg-[#0F1015] border-[#2E3242] focus-visible:ring-red-600 h-10" />
                <Button data-testid="admin-announce-send" onClick={sendAnnounce} disabled={!announce.trim() || sending} className="ruby-gradient text-white border border-red-900/40 h-10 px-4">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post"}
                </Button>
              </div>
            </div>

            <p className="mt-7 mb-2 text-[11px] uppercase tracking-wider text-gray-500">All members ({data.users.length})</p>
            <div className="rounded-xl border border-[#2E3242] overflow-hidden">
              {data.users.map((u) => (
                <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center gap-3 px-3 sm:px-4 py-3 border-b border-[#2E3242] last:border-0 bg-[#1E2029] hover:bg-[#232634] transition-colors">
                  <div className="relative shrink-0">
                    <img src={u.avatar} alt={u.name} className="w-9 h-9 rounded-full object-cover" />
                    {u.online && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[#1E2029]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-100 truncate">{u.name}</p>
                      {u.role === "admin" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-950/60 text-red-300 border border-red-900/50">admin</span>}
                    </div>
                    <p className="text-[11px] text-gray-500 truncate">{u.email}</p>
                    <p className="text-[10px] text-gray-600 mt-0.5">
                      {u.message_count} msgs · {u.tasks_created} tasks · {u.online ? "online" : `seen ${relTime(u.last_seen)}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`admin-edit-${u.id}`} onClick={() => setEditing(u)} className="w-8 h-8 rounded-md flex items-center justify-center text-gray-500 hover:text-blue-300 border border-[#2E3242]" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {u.id !== user?.id && (
                      <button data-testid={`admin-delete-${u.id}`} onClick={() => removeUser(u)} className="w-8 h-8 rounded-md flex items-center justify-center text-gray-500 hover:text-red-400 border border-[#2E3242]" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <EditUserDialog
        target={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={(u) => setData((d) => ({ ...d, users: d.users.map((x) => (x.id === u.id ? { ...x, ...u } : x)) }))}
      />
    </div>
  );
}
